
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAdminAuth } from '../context/AdminAuthContext';

// Language switcher component
function LanguageSwitcher() {
  const { i18n } = useTranslation();
  return (
    <select
      value={i18n.language}
      onChange={e => i18n.changeLanguage(e.target.value)}
      className="rounded-md border-slate-200 bg-slate-50 py-1 pl-2 pr-6 text-xs font-medium text-slate-700 focus:border-teal-500 focus:ring-teal-500"
    >
      <option value="en">English</option>
      <option value="ar">العربية</option>
    </select>
  );
}


export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const { isAdmin } = useAdminAuth();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: "header.home", default: "Home" },
    { to: "/about", label: "header.about", default: "About Us" },
    { to: "/admin-panel", label: "header.admin_panel", default: "Admin Panel" },
    { to: "/mobile-app", label: "header.mobile_app", default: "Mobile App" },
    { to: "/driver-app", label: "header.driver_app", default: "Driver App" },
    { to: "/web-app", label: "header.web_app", default: "Web App" },
    { to: "/blog", label: "header.blogs", default: "Blogs" },
    { to: "/testimonials", label: "header.testimonials", default: "Testimonials" },
    { to: "/contact", label: "header.contact", default: "Contact" },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="bg-slate-900 text-white text-xs py-2">
        <div className="mx-auto max-w-7xl px-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2 opacity-90 hover:opacity-100 transition">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
              783-124-226
            </span>
            <a href="mailto:info@elorayem.com" className="hidden sm:flex items-center gap-2 opacity-90 hover:opacity-100 transition">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
              info@elorayem.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link
                to="/admin"
                className="text-xs font-semibold px-3 py-1 rounded bg-slate-800 hover:bg-teal-600 transition text-white border border-slate-700"
                style={{ textDecoration: 'none' }}
              >
                Admin App
              </Link>
            )}
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header 
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-sm shadow-md py-3" : "bg-white py-8"
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="/images/logo-dark.png" 
              alt="Elora" 
              className={`object-contain transition-all duration-300 ${scrolled ? "h-14" : "h-32"}`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link 
                key={link.to} 
                to={link.to} 
                className={`text-lg font-medium transition-colors hover:text-teal-600 ${
                  location.pathname === link.to ? "text-teal-600" : "text-slate-600"
                }`}
              >
                {t(link.label, link.default)}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-5">
            <Link to="/contact" className="btn-primary py-4 px-8 text-lg">
              {t("header.cta_get_started")}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-600 hover:text-teal-600 transition"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
            )}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileOpen && (
          <div className="absolute top-full left-0 w-full bg-white border-t border-slate-100 shadow-lg lg:hidden animate-fade-in max-h-[calc(100vh-80px)] overflow-y-auto">
            <div className="p-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link 
                  key={link.to} 
                  to={link.to} 
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    location.pathname === link.to 
                      ? "bg-teal-50 text-teal-700" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  {t(link.label, link.default)}
                </Link>
              ))}
              <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col gap-3">
                <Link to="/contact" className="w-full btn-primary justify-center">
                  {t("header.cta_get_started")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
